// ---------------------------------------------------------------------------
// Pure (non-React) i18n primitives — split out from context.tsx so that
// model-layer code reachable from a Web Worker (csvWorker.ts) can import
// `translate`/`Translate`/`TranslatableError` without pulling in React. A
// plain .ts file imported from a Worker is fine; a .tsx file is not — in
// Vite dev mode, the React Fast Refresh preamble it injects into every .tsx
// module references `window`, which doesn't exist in a Worker (only `self`),
// so the worker's module graph throws immediately on load. context.tsx
// re-exports everything here for the rest of the app.
// ---------------------------------------------------------------------------
import type { Language } from "../model/language.ts";
import { en } from "./en.ts";
import { es, type TextKey } from "./es.ts";

const DICTIONARIES: Record<Language, Record<TextKey, string>> = { es, en };

/** Shape of `useT()`'s return value / `translate` partially applied to a `language` — for model-layer functions that need to produce translated text but aren't React components (see entityNames.ts, subBasinState.ts, project.ts...). */
export type Translate = (key: TextKey, params?: Record<string, string | number>) => string;

/**
 * `translate(language, key, params?)` — hook-less version, for the few
 * places that can't use `useT()` because they aren't function components
 * (ErrorBoundary.tsx, a `class Component`) or run outside the React tree.
 * The value can carry `{name}`-style placeholders, substituted with
 * `params.name` (see es.ts/en.ts).
 */
export function translate(
  language: Language,
  key: TextKey,
  params?: Record<string, string | number>
): string {
  const text = DICTIONARIES[language][key];
  if (!params) return text;
  return Object.entries(params).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), text);
}

/**
 * An `Error` carrying an i18n key instead of already-resolved text — for
 * model-layer functions (projectEntities.ts, dailySeries.ts, gisUpload.ts,
 * gisLayer.ts, exportProject.ts...) that validate/parse and throw on bad
 * input, but are called from MANY different UI call sites, each with its own
 * `t()` in scope. Threading a `Translate` parameter into every one of those
 * functions (and updating every call site) would be a much bigger ripple
 * than catching a typed error and translating it once, right where it's
 * displayed — see `translateError`.
 */
export class TranslatableError extends Error {
  constructor(
    public readonly key: TextKey,
    public readonly params?: Record<string, string | number>
  ) {
    super(key);
  }
}

/**
 * Drop-in replacement for the `err instanceof Error ? err.message : String(err)`
 * pattern used in every `catch` block across the app — translates a
 * `TranslatableError` via `t`, falls back to `.message`/`String(err)` for any
 * other thrown value (a real bug, a browser API error...).
 */
export function translateError(err: unknown, t: Translate): string {
  if (err instanceof TranslatableError) return t(err.key, err.params);
  return err instanceof Error ? err.message : String(err);
}
